import React from 'react';
import '../../GridEditor/ItemBuildProgression.css';
import RecipeTree from '../../GridEditor/RecipeTree';

// Item details interface (should match your existing ItemDetails type)
export interface ItemDetails {
  id: string;
  name: string;
  cost: number;
  image?: {
    full: string;
  };
  stats?: Record<string, number>;
  passives?: Array<{
    name: string;
    description: string;
  }>;
}

interface Props {
  item: ItemDetails;
  mousePosition: { x: number; y: number };
  apiVersion: string;
  className?: string;
}

const ItemTooltip: React.FC<Props> = ({
  item,
  mousePosition,
  apiVersion,
  className = ''
}) => {
  
  // Map stat keys to their icon class names
  const STAT_ICON_MAP: Record<string, string> = {
    'attackDamage': 'attack-damage-icon',
    'abilityPower': 'ability-power-icon',
    'armor': 'armor-icon',
    'magicResistance': 'magic-resist-icon',
    'attackSpeed': 'attack-speed-icon',
    'abilityHaste': 'ability-haste-icon',
    'health': 'health-icon',
    'mana': 'resource-icon',
    'movementSpeed': 'movement-speed-icon',
    'criticalStrikeChance': 'crit-chance-icon',
    'criticalStrikeDamage': 'crit-damage-icon',
    'critDamage': 'crit-chance-icon',
    'lifeSteal': 'life-steal-icon',
    'omnivamp': 'omnivamp-icon',
    'armorPenetrationPercent': 'armor-penetration-icon',
    'lethality': 'armor-penetration-icon',
    'magicPenetration': 'magic-penetration-icon',
    'magicPenetrationPercent': 'magic-penetration-icon',
    'tenacity': 'tenacity-icon',
    'baseHealthRegenPercent': 'health-regen-icon',
    'baseManaRegenPercent': 'resource-regen-icon',
    'baseHealthRegen': 'health-regen-icon',
    'baseManaRegen': 'resource-regen-icon',
    'healAndShieldPower': 'heal-shield-power-icon',
    'gold': 'gold-icon',
  };

  // Function to get a display name for a stat key
  const getStatDisplayName = (statKey: string): string => {
    const displayNameMap: Record<string, string> = {
      'attackDamage': 'Attack Damage',
      'abilityPower': 'Ability Power',
      'armor': 'Armor',
      'magicResistance': 'Magic Resist',
      'attackSpeed': 'Attack Speed',
      'abilityHaste': 'Ability Haste',
      'health': 'Health',
      'mana': 'Mana',
      'movementSpeed': 'Movement Speed',
      'criticalStrikeChance': 'Critical Strike',
      'critDamage': 'Critical Damage',
      'lifeSteal': 'Life Steal',
      'omnivamp': 'Omnivamp',
      'armorPenetrationPercent': 'Armor Penetration (%)',
      'lethality': 'Lethality',
      'magicPenetration': 'Magic Penetration',
      'magicPenetrationPercent': 'Magic Penetration (%)',
      'tenacity': 'Tenacity',
      'healthRegen': 'Health Regen',
      'manaRegen': 'Mana Regen'
    };
    
    return displayNameMap[statKey] || statKey;
  };

  return (
    <div 
      className={`item-tooltip ${className}`}
      style={{
        position: 'fixed',
        top: `${mousePosition.y + 10}px`,
        left: `${mousePosition.x + 10}px`,
        zIndex: 10000,
      }}
    >
      <div className="tooltip-content">
        <div className="item-header">
          <img 
            src={`https://ddragon.leagueoflegends.com/cdn/${apiVersion}/img/item/${item?.image?.full || 'missing.png'}`} 
            alt={item?.name || 'Item'}
            className="tooltip-item-image"
          />
          <div className="tooltip-item-info">
            <div className="tooltip-item-name">{item?.name}</div>
            <div className="tooltip-item-cost">
              <div className="stat-icon gold-icon"></div>
              {item?.cost}
            </div>
          </div>
        </div>
        
        {/* Two-column layout for details */}
        <div className="tooltip-details-container">
          {/* Left side - Stats and Passives */}
          <div className="tooltip-left-column">
            {/* Stats section */}
            {item?.stats && Object.keys(item.stats).length > 0 && (
              <div className="tooltip-stats">
                <h6>Stats</h6>
                <div className="tooltip-stats-list">
                  {Object.entries(item.stats).map(([key, value]) => (
                    <div key={key} className="tooltip-stat-row">
                      <div className={`stat-icon ${STAT_ICON_MAP[key] || 'default-stat-icon'}`}></div>
                      <div className="tooltip-stat-value">{value}</div>
                      <div className="tooltip-stat-name">{getStatDisplayName(key)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Passives section */}
            {item?.passives && item.passives.length > 0 && (
              <div className="tooltip-passives">
                <h6>Passives</h6>
                <div className="tooltip-passives-list">
                  {item.passives.map((passive, index) => (
                    <div key={index} className="tooltip-passive">
                      <div className="tooltip-passive-name">{passive.name}</div>
                      <div className="tooltip-passive-description">{passive.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Right side - Recipe Tree */}
          <div className="tooltip-right-column">
            <div className="tooltip-recipe-section">
              <h6>Recipe</h6>
              <div className="tooltip-recipe-tree">
                <RecipeTree 
                  itemId={parseInt(item?.id || '0')}
                  apiVersion={apiVersion}
                  className="tooltip-recipe-tree-component"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemTooltip;
